/**
 * Das ⋯-/Konto-Menü-Muster: ein Auslöser öffnet ein `role="menu"`-Popover,
 * ein Klick daneben oder `Esc` schließt es wieder.
 *
 * Geteilte Form für `router.tsx::AccountMenu` und
 * `features/billing/TenantLedger.tsx::LedgerRowMenu` — beide bauten zuvor
 * dasselbe Popover Zeichen für Zeichen nach, `LedgerRowMenu` dabei ohne den
 * `Esc`-Handler von `AccountMenu` (`simplifier`/`gatekeeper`,
 * `docs/work/004c-storno-mietkonto-6a1662/reports/15-simplifier.md`,
 * Befund G1/W5-Nachbarschaft). Liegt in `components/`, weil beide Aufrufer es
 * brauchen und Features einander nicht kennen (`apps/web/CLAUDE.md`).
 */

import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'

const MENU_ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]'

// Bringt der Aufrufer eine eigene Position mit (`absolute` …), setzt der
// Wrapper kein `relative`: Beide Klassen stehen sonst gleichrangig im CSS,
// und `relative` gewinnt über die Reihenfolge. Ein positioniertes Element
// bleibt Bezugsrahmen für das Menü.
const OWN_POSITION = /(^|\s)(absolute|fixed|sticky)(\s|$)/

interface PopoverMenuProps {
  readonly triggerTestId: string
  readonly triggerLabel: string
  readonly triggerClassName: string
  readonly menuTestId?: string
  readonly menuClassName: string
  readonly wrapperClassName?: string
  readonly trigger: ReactNode
  /** Seit Spec 39 (Objektfilter der früheren Aufgabenleiste): `dialog` für ein Popover, das
   * keine Menüeinträge, sondern ein eigenes Bedienelement trägt (dort den
   * Objektpicker mit Suchfeld). Vorgabe `menu` — das bisherige Verhalten. */
  readonly popupRole?: 'menu' | 'dialog'
  readonly children: (close: () => void) => ReactNode
}

export function PopoverMenu({
  triggerTestId,
  triggerLabel,
  triggerClassName,
  menuTestId,
  menuClassName,
  wrapperClassName,
  trigger,
  popupRole = 'menu',
  children,
}: PopoverMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false)
  const hasOwnPosition = OWN_POSITION.test(wrapperClassName ?? '')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  // Esc schließt — dieselbe Erwartung wie bei jedem Overlay (`design-system`).
  // Der Fokus kehrt zum Auslöser zurück, sonst stünde er nach dem Schließen
  // auf `body` (Spec 39, „Tastatur").
  useEffect(() => {
    if (!isOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setIsOpen(false)
      triggerRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [isOpen])

  // Mit der Tastatur geöffnet, springt der Fokus in das Popover — auf den
  // gewählten Eintrag eines Menüs, sonst auf das erste bedienbare Element.
  // Mit der Maus geöffnet bleibt er, wo er war (bisheriges Verhalten,
  // keine Fokusmarkierung in Aufnahmen).
  useEffect(() => {
    if (!isOpen || !openedByKeyboard) return
    const popup = popupRef.current
    if (popup === null) return
    const target =
      popup.querySelector<HTMLElement>('[aria-checked="true"]') ??
      popup.querySelector<HTMLElement>(popupRole === 'menu' ? MENU_ITEM_SELECTOR : 'input, button, [href]')
    target?.focus()
  }, [isOpen, openedByKeyboard, popupRole])

  // Pfeiltasten wandern durch die Einträge eines Menüs (WAI-ARIA Menu
  // Button), Pos1/Ende springen an die Ränder.
  function onMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (popupRole !== 'menu') return
    const items = [...event.currentTarget.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR)]
    if (items.length === 0) return
    const current = items.indexOf(document.activeElement as HTMLElement)
    const next =
      event.key === 'ArrowDown'
        ? (current + 1) % items.length
        : event.key === 'ArrowUp'
          ? (current - 1 + items.length) % items.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : undefined
    if (next === undefined) return
    event.preventDefault()
    items[next]?.focus()
  }

  return (
    <div className={`${hasOwnPosition ? '' : 'relative'} ${wrapperClassName ?? ''}`}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={triggerTestId}
        aria-haspopup={popupRole}
        aria-expanded={isOpen}
        aria-label={triggerLabel}
        onClick={(event) => {
          // `detail === 0`: über Enter/Leertaste ausgelöst, nicht per Zeiger.
          setOpenedByKeyboard(event.detail === 0)
          setIsOpen((open) => !open)
        }}
        className={triggerClassName}
      >
        {trigger}
      </button>

      {isOpen && (
        <>
          {/* Klick daneben schließt. Ein eigenes Element statt eines
              Blur-Handlers, damit auch ein Klick auf den Inhalt schließt. */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => {
              setIsOpen(false)
            }}
          />
          <div
            ref={popupRef}
            role={popupRole}
            aria-label={popupRole === 'dialog' ? triggerLabel : undefined}
            data-testid={menuTestId}
            className={menuClassName}
            onKeyDown={onMenuKeyDown}
          >
            {children(() => {
              setIsOpen(false)
            })}
          </div>
        </>
      )}
    </div>
  )
}
